import { useState, useRef, useEffect } from "react";
import { useAdmin } from "../../contexts/AdminContext";
import { sendChatMessage, fetchChatMessages, saveChatSession } from "../../lib/api";

interface Message {
  id: number;
  from: "bot" | "user" | "admin";
  text: string;
  time: string;
}

const fmtTime = (date?: string) => {
  const d = date ? new Date(date) : new Date();
  return d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
};

const botReplies: Record<string, string> = {
  default: "Vielen Dank für Ihre Nachricht! Ein Mitarbeiter wird Ihnen in Kürze antworten. In der Zwischenzeit können Sie gerne unsere FAQ lesen.",
  ticket: "Ihre Teilnahmetickets werden Ihnen innerhalb von 2 Minuten nach Zahlungsbestätigung per E-Mail zugesandt. Bitte überprüfen Sie Ihren Spam-Ordner, falls Sie diese nicht erhalten.",
  paiement: "Wir akzeptieren PayPal, Visa, Mastercard, Klarna, Apple Pay, Google Pay und SEPA-Überweisung. Alle Zahlungen sind SSL-verschlüsselt.",
  tirage: "Die nächste Verlosung #52 ist für den 15. Juni 2026 geplant. Sie wird von einem unabhängigen Notar überwacht und live übertragen.",
  compte: "Bei Login-Problemen klicken Sie bitte auf 'Passwort vergessen' auf der Login-Seite. Ein Link wird Ihnen innerhalb von 2 Minuten zugesandt.",
  gagner: "Wenn Sie gewinnen, werden Sie innerhalb von 24 Stunden nach der Verlosung per E-Mail und Telefon kontaktiert. Die Übergabe der Villa erfolgt persönlich.",
};

const quickReplies = [
  { label: "Wo sind meine Tickets?", key: "ticket" },
  { label: "Zahlungsmethoden", key: "paiement" },
  { label: "Datum der Verlosung", key: "tirage" },
  { label: "Kontoprobleme", key: "compte" },
];

const getBotReply = (text: string): string => {
  const lower = text.toLowerCase();
  if (lower.includes("ticket") || lower.includes("teilnahme")) return botReplies.ticket;
  if (lower.includes("zahlung") || lower.includes("bezahlen") || lower.includes("karte") || lower.includes("paypal")) return botReplies.paiement;
  if (lower.includes("ziehung") || lower.includes("datum") || lower.includes("wann")) return botReplies.tirage;
  if (lower.includes("konto") || lower.includes("login") || lower.includes("passwort")) return botReplies.compte;
  if (lower.includes("gewinn") || lower.includes("gewinner") || lower.includes("preis")) return botReplies.gagner;
  return botReplies.default;
};

// Generate or retrieve session ID
const getSessionId = () => {
  let id = sessionStorage.getItem("thv_chat_session");
  if (!id) {
    id = "session_" + Math.random().toString(36).substring(2, 11);
    sessionStorage.setItem("thv_chat_session", id);
  }
  return id;
};

export default function LiveChat() {
  const { settings } = useAdmin();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [unread, setUnread] = useState(0);
  const [identified, setIdentified] = useState(() => !!localStorage.getItem("thv_chat_name"));
  const [idForm, setIdForm] = useState({ name: localStorage.getItem("thv_chat_name") || "", email: "" });
  const bottomRef = useRef<HTMLDivElement>(null);
  const sessionId = getSessionId();

  const isThirdParty = !!settings.thirdPartyChatScript;

  // Poll for messages
  useEffect(() => {
    if (!settings.chatEnabled || isThirdParty || !identified) return;

    const load = async () => {
      try {
        const data = await fetchChatMessages(sessionId);
        const mapped = data.map((m: any) => ({
          id: m.id,
          from: m.sender,
          text: m.text,
          time: fmtTime(m.created_at)
        }));

        if (mapped.length === 0 && messages.length === 0) {
            setMessages([{
                id: 0,
                from: "bot",
                text: settings.chatWelcomeMessage || "Hallo! Wie kann ich Ihnen heute helfen?",
                time: fmtTime()
            }]);
        } else if (mapped.length > messages.length) {
            const lastMsg = mapped[mapped.length - 1];
            if (!open && lastMsg.from === 'admin' && lastMsg.id > (messages[messages.length-1]?.id || 0)) {
                setUnread(prev => prev + 1);
            }
            setMessages(mapped);
        }
      } catch (err) {
        console.error("Failed to fetch messages:", err);
      }
    };

    load();
    const iv = setInterval(load, 3000);
    return () => clearInterval(iv);
  }, [sessionId, messages.length, open, settings.chatEnabled, isThirdParty, settings.chatWelcomeMessage, identified]);

  useEffect(() => {
    if (isThirdParty) {
      const div = document.createElement("div");
      div.innerHTML = settings.thirdPartyChatScript;
      const scripts = div.getElementsByTagName("script");
      for (let i = 0; i < scripts.length; i++) {
        const s = document.createElement("script");
        if (scripts[i].src) s.src = scripts[i].src;
        s.innerHTML = scripts[i].innerHTML;
        document.head.appendChild(s);
      }
    }
  }, [isThirdParty, settings.thirdPartyChatScript]);

  useEffect(() => {
    if (open) {
      setUnread(0);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    }
  }, [open, messages, identified]);

  if (!settings.chatEnabled) return null;
  if (isThirdParty) return null;

  const handleIdentification = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!idForm.name.trim()) return;
    localStorage.setItem("thv_chat_name", idForm.name);
    try {
        await saveChatSession(sessionId, idForm.name, idForm.email);
        setIdentified(true);
    } catch (err) {
        console.error("Failed to save session:", err);
        setIdentified(true); // Don't block user if server fails
    }
  };

  const sendMessage = async (text: string) => {
    if (!text.trim()) return;
    
    try {
        await sendChatMessage(sessionId, 'user', text.trim());
    } catch (err) {
        console.error("Send failed:", err);
    }

    setInput("");
    setIsTyping(true);

    setTimeout(async () => {
      const reply = getBotReply(text);
      await sendChatMessage(sessionId, 'bot', reply);
      setIsTyping(false);
    }, 1500);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  return (
    <>
      {/* Chat Panel */}
      <div
        className={`fixed bottom-24 right-6 z-[90] w-80 bg-white rounded-2xl border border-slate-200 flex flex-col overflow-hidden transition-all duration-300 origin-bottom-right ${
          open ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-90 pointer-events-none"
        }`}
        style={{ height: 480, boxShadow: "0 8px 40px rgba(0,0,0,0.12)" }}
      >
        {/* Header */}
        <div className="bg-slate-900 px-4 py-3 flex items-center gap-3 flex-shrink-0">
          <div className="relative">
            <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
              <i className="ri-customer-service-2-line text-white text-base"></i>
            </div>
            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-secondary-light rounded-full border-2 border-slate-900"></div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-semibold text-sm leading-none">{settings.chatBotName || "Support"}</p>
            <p className="text-secondary-light text-xs mt-0.5">{settings.chatResponseTime || "Online"}</p>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="w-7 h-7 flex items-center justify-center text-white/60 hover:text-white transition-colors cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {!identified ? (
          /* Identification Form */
          <div className="flex-1 flex flex-col p-6 bg-slate-50 justify-center">
             <div className="text-center mb-6">
                <i className="ri-shield-user-line text-4xl text-slate-300"></i>
                <h4 className="text-slate-900 font-bold mt-2">Willkommen beim Support</h4>
                <p className="text-slate-500 text-xs mt-1">Bitte hinterlassen Sie Ihre Daten, damit wir Ihnen besser helfen können.</p>
             </div>
             <form onSubmit={handleIdentification} className="space-y-4">
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1 px-1">Ihr Name *</label>
                  <input 
                    type="text" 
                    required
                    value={idForm.name}
                    onChange={(e) => setIdForm({...idForm, name: e.target.value})}
                    placeholder="Vorname & Nachname"
                    className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2 text-sm focus:border-secondary outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1 px-1">Ihre E-Mail (optional)</label>
                  <input 
                    type="email" 
                    value={idForm.email}
                    onChange={(e) => setIdForm({...idForm, email: e.target.value})}
                    placeholder="email@beispiel.de"
                    className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2 text-sm focus:border-secondary outline-none transition-colors"
                  />
                </div>
                <button
                  type="submit"
                  className="w-full bg-slate-900 text-white font-bold py-3 rounded-xl text-sm hover:bg-slate-800 transition-colors cursor-pointer shadow-lg shadow-slate-200"
                >
                  Chat starten
                </button>
             </form>
          </div>
        ) : (
          /* Messages */
          <>
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50">
              {messages.map((msg) => (
                <div key={msg.id} className={`flex ${msg.from === "user" ? "justify-end" : "justify-start"}`}>
                  {msg.from !== "user" && (
                    <div className={`w-6 h-6 rounded-full ${msg.from === 'admin' ? 'bg-primary' : 'bg-slate-900'} flex items-center justify-center flex-shrink-0 mr-2 mt-0.5`}>
                      <i className={`${msg.from === 'admin' ? 'ri-user-star-line' : 'ri-robot-line'} text-white text-[10px]`}></i>
                    </div>
                  )}
                  <div className="max-w-[75%]">
                    <div
                      className={`px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                        msg.from === "user"
                          ? "bg-slate-900 text-white rounded-tr-sm"
                          : "bg-white border border-slate-100 text-slate-700 rounded-tl-sm shadow-sm"
                      }`}
                    >
                      {msg.text}
                    </div>
                    <p className={`text-[10px] text-slate-400 mt-0.5 ${msg.from === "user" ? "text-right" : "text-left"}`}>
                      {msg.from === 'admin' ? 'Agent · ' : ''}{msg.time}
                    </p>
                  </div>
                </div>
              ))}

              {isTyping && (
                <div className="flex justify-start items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-slate-900 flex items-center justify-center flex-shrink-0">
                    <i className="ri-robot-line text-secondary text-xs"></i>
                  </div>
                  <div className="bg-white border border-slate-100 rounded-2xl rounded-tl-sm px-4 py-2.5 flex gap-1 items-center">
                    <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></div>
                    <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></div>
                    <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></div>
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            {/* Quick Replies */}
            <div className="px-3 pt-2 pb-1 flex gap-1.5 flex-wrap bg-white border-t border-slate-100 flex-shrink-0">
              {quickReplies.map((qr) => (
                <button
                  key={qr.key}
                  onClick={() => sendMessage(qr.label)}
                  className="text-[11px] bg-slate-100 hover:bg-secondary/10 hover:text-secondary-dark text-slate-600 font-medium px-2.5 py-1 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                >
                  {qr.label}
                </button>
              ))}
            </div>

            {/* Input */}
            <form onSubmit={handleSubmit} className="px-3 pb-3 pt-2 bg-white flex gap-2 flex-shrink-0">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Schreiben Sie Ihre Nachricht..."
                className="flex-1 bg-slate-50 border border-slate-200 rounded-full px-4 py-2 text-sm focus:outline-none focus:border-secondary transition-colors"
              />
              <button
                type="submit"
                disabled={!input.trim()}
                className="w-9 h-9 bg-slate-900 hover:bg-slate-800 disabled:opacity-40 text-white rounded-full flex items-center justify-center flex-shrink-0 transition-colors cursor-pointer"
              >
                <i className="ri-send-plane-fill text-sm"></i>
              </button>
            </form>
          </>
        )}
      </div>

      {/* Floating Button */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-[90] w-14 h-14 bg-slate-900 hover:bg-slate-800 text-white rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer"
        style={{ boxShadow: "0 4px 24px rgba(0,0,0,0.18)" }}
        aria-label="Chat öffnen"
      >
        {open ? (
          <i className="ri-close-line text-2xl"></i>
        ) : (
          <>
            <i className="ri-message-3-line text-2xl"></i>
            {unread > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {unread}
              </span>
            )}
          </>
        )}
      </button>
    </>
  );
}
